import classNames from 'classnames';
import React from 'react';
import { TFunction } from 'react-i18next';
import { REQUIREMENTS_API_BASE } from '../../../../api/api';
import { updateAPIRequest } from '../../../../api/useGenericApi';
import { ProButton } from '../../../../atoms/Button/ProButton';
import { ProIconButton } from '../../../../atoms/Button/ProIconButton';
import { ActionCell } from '../../../../atoms/Cells/ActionCell';
import { ProIcon } from '../../../../atoms/Icons/Icon';
import { ProIconData, ProIconDataButtons } from '../../../../atoms/Icons/IconData';
import { TableColData } from '../../../../atoms/Table/Table';
import {
    WindowDisplayState,
    getIconForTypeInNameCell,
} from '../../../../modules/Windows/WBSElement/WBSElement.helpers';
import { ProIntent, ProSize } from '../../../../utils/enums';
import { showErrorToast } from '../../../../utils/helpers';
import { Components } from '../../../../utils/types';
import {
    getReqCellIcon,
    getReqStatusIcon,
    isReqNotBacklog,
    isTaskNotReq,
} from '../../Requirements/components/RequirementTableNameCell';

// Display tasks here?
export const getSprintPlanningTableData = ({
    requirements,
    selectElement,
    t,
    deleteAction,
    isDisplayed,
    mode,
    portfolio,
    selectedSprint,
    sync,
}: {
    selectElement: (id?: string) => void;
    requirements: Components.Schemas.RequirementDTO[];
    t: TFunction;
    isDisplayed: boolean;
    mode: WindowDisplayState;
    deleteAction: (i: number) => void;
    portfolio?: Components.Schemas.PortfolioDTO;
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    sync: () => void;
}): TableColData[] => [
    {
        name: 'Requirement',
        cellRender: (i) => {
            return (
                <>
                    <div className="ProTable__levelBars">
                        <div className={`ProTable__levelBar ProTable__levelBar-1`}>1</div>
                    </div>
                    <span className="ProTable__name" onClick={() => selectElement(requirements[i].id!)}>
                        <ProIcon size={ProSize.Small} icon={getReqCellIcon(requirements[i])} />
                        {requirements[i].name ?? 'not named'}
                    </span>
                </>
            );
        },
        className: 'TableRowName ProTable__levels',
        colSpace: 'extend',
    },
    {
        name: t('table.cols.action'),
        cellRender: (i) => (
            <div className="ProTable--actionCell">
                <ProButton
                    text={'Take to sprint'}
                    mainIntent={ProIntent.Success}
                    leftIcon={ProIconDataButtons.add_xs}
                    onClick={() => takeToSprint({ portfolio, req: requirements[i], selectedSprint, sync })}
                />
            </div>
        ),
        className: `ProTable__buttons`,
        colSpace: 4,
    },
    {
        name: 'Status',
        cellRender: (i) => (
            <>{getReqStatusIcon(requirements[i].requirementType, requirements[i].requirementState?.name)}</>
        ),
        colSpace: 3,
        className: 'ProTable__statusIcon',
    },
    {
        name: 'Responsible',
        cellRender: (i) =>
            isReqNotBacklog(requirements[i])
                ? `${requirements[i].responsiblePerson?.firstName ?? ''} ${
                      requirements[i].responsiblePerson?.lastName ?? '-'
                  }`
                : '',
        colSpace: 4,
        hidden: () => !isDisplayed,
    },
    {
        name: 'Scope',
        cellRender: (i) => {
            if (isTaskNotReq(requirements[i])) return '-';
            return `${(requirements[i] as Components.Schemas.RequirementDTO).storyPoints} SP`;
        },
        colSpace: 2,
        hidden: () => !isDisplayed,
    },
    {
        name: 'Benefit',
        cellRender: (i) => {
            if (isTaskNotReq(requirements[i])) return '-';
            return `${(requirements[i] as Components.Schemas.RequirementDTO).businessValue} BP`;
        },
        colSpace: 2,
        hidden: () => !isDisplayed,
    },
    {
        name: 'Priority',
        cellRender: (i) => (isReqNotBacklog(requirements[i]) ? requirements[i].requirementPriorityState?.name : ''),
        colSpace: 2,
        hidden: () => !isDisplayed,
    },
    {
        name: t('table.cols.action'),
        cellRender: (i) => (
            <ActionCell
                i={i}
                selectTableRow={() => selectElement(requirements[i].id!)}
                detailWindowMode={[mode]}
                deleteAction={() => {
                    deleteAction(i);
                }}
            />
        ),

        colSpace: 2,
    },
];

export const getPlanningSprintTableData = ({
    selectElement,
    t,
    selectSprint,
    selectedSprint,
    sprintOpen,
    toggleSprintOpen,
    wbsIsDisplayed,
    sync,
    portfolio,
}: {
    selectElement: (id?: string) => void;
    t: TFunction;
    selectSprint: () => void;
    sprintOpen: boolean;
    toggleSprintOpen: () => void;
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    wbsIsDisplayed: boolean;
    portfolio?: Components.Schemas.PortfolioDTO;
    sync: () => void;
}): TableColData[] => [
    {
        name: 'Sprint',
        cellRender: (i) => {
            if (i === 0)
                return (
                    <>
                        <div className="ProTable__levelBars ">
                            <div className={classNames('ProTable__levelBar', `ProTable__levelBar-1`)}>1</div>
                        </div>

                        <div
                            className={classNames('ProTable__level', sprintOpen && 'ProTable__level--open')}
                            onClick={toggleSprintOpen}
                        >
                            <ProIcon icon={ProIconData.chevron_outline_down} customSize="14" />
                        </div>

                        <span className="ProTable__name" onClick={() => selectSprint()}>
                            {selectedSprint.wbsElementType && getIconForTypeInNameCell(selectedSprint.wbsElementType)}
                            {selectedSprint.name}
                        </span>
                    </>
                );
            else
                return (
                    <>
                        <div className="ProTable__levelBars ">
                            <div className="ProTable__levelBar ProTable__levelBar-2">1</div>
                        </div>
                        <span className="ProTable__name" onClick={() => selectSprint()}>
                            <ProIcon size={ProSize.Small} icon={ProIconData.base_data} />
                            {selectedSprint.requirements && selectedSprint.requirements[i - 1].name}
                        </span>
                    </>
                );
        },
        className: 'TableRowName ProTable__levels',
        colSpace: 'extend',
    },
    {
        name: t('table.cols.action'),
        cellRender: (i) =>
            i > 0 && (
                <div className="ProTable--actionCell">
                    <ProButton
                        text={'Remove from sprint'}
                        mainIntent={ProIntent.Edit}
                        leftIcon={ProIconDataButtons.bin_xs}
                        onClick={() =>
                            removeFromSprint({
                                portfolio,
                                req: selectedSprint.requirements![i - 1],
                                selectedSprint,
                                sync,
                            })
                        }
                    />
                </div>
            ),
        className: `ProTable__buttons`,
        colSpace: 5,
    },
    {
        name: t('table.cols.responsible'),
        cellRender: (i) =>
            selectedSprint.responsiblePerson
                ? `${selectedSprint.responsiblePerson?.firstName ?? ''} ${
                      selectedSprint.responsiblePerson?.lastName ?? 'no data'
                  }`
                : '',
        hidden: () => !wbsIsDisplayed,
        colSpace: 4,
    },
    {
        name: t('table.cols.constraint'),
        cellRender: (i) => selectedSprint.constraintQualityType,
        hidden: () => !wbsIsDisplayed,
        colSpace: 4,
    },
    {
        name: 'SP',
        cellRender: (i) =>
            i === 0
                ? `${selectedSprint.scopePoints} SP`
                : selectedSprint?.requirements &&
                  selectedSprint?.requirements[i - 1] &&
                  `${selectedSprint?.requirements[i - 1].storyPoints} SP`,
        colSpace: 1,
    },

    {
        name: t('table.cols.action'),
        cellRender: (i) => (
            <div className="ProTable--actionCell">
                <ProIconButton
                    icon={ProIconDataButtons.arrow_right_xs}
                    tooltip={t('buttons.open')}
                    mainIntent={ProIntent.Transparent}
                    hoverIntent={ProIntent.Secondary}
                    size={ProSize.XSmall}
                    onClick={() =>
                        i === 0
                            ? selectSprint()
                            : selectElement(
                                  selectedSprint.requirements ? selectedSprint.requirements[i - 1].id : undefined
                              )
                    }
                    lineIntent={ProIntent.Secondary}
                    placement="bottom"
                />
            </div>
        ),
        className: `ProTable__buttons`,
        colSpace: 2,
    },
];

export const takeToSprint = ({
    portfolio,
    req,
    selectedSprint,
    sync,
}: {
    req: Components.Schemas.RequirementDTO;
    portfolio?: Components.Schemas.PortfolioDTO;
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    sync: () => void;
}) => {
    const updateReq = (req: Components.Schemas.RequirementDTO) => {
        updateAPIRequest(req, REQUIREMENTS_API_BASE, req.id!)
            .then(() => {
                sync();
            })
            .catch(() => {
                showErrorToast('Cant update element.');
            });
    };

    // const updateSprint = (sprint: Components.Schemas.WBSElementDeltaDTO) => {
    //     updateAPIRequest(sprint, WBS_ELEMENT_BASE_URL, sprint.id!)
    //         .then(() => {
    //             sync();
    //         })
    //         .catch(() => {
    //             showErrorToast('Cant update element.');
    //         });
    // };

    const scheduled = portfolio?.requirementStates?.find((cur) => cur.name === 'scheduled');
    const newReq: Components.Schemas.RequirementDTO = {
        ...req,
        requirementState: scheduled,
        wbsElement: selectedSprint,
    };
    updateReq(newReq);
    // const newSprint: Components.Schemas.WBSElementDeltaDTO = {
    //     ...selectedSprint,
    //     requirements: [...(selectedSprint.requirements || []), newReq],
    // };
    // updateSprint(newSprint);
};

export const removeFromSprint = ({
    portfolio,
    req,
    sync,
}: {
    req: Components.Schemas.RequirementDTO;
    portfolio?: Components.Schemas.PortfolioDTO;
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    sync: () => void;
}) => {
    const updateReq = (req: Components.Schemas.RequirementDTO) => {
        updateAPIRequest(req, REQUIREMENTS_API_BASE, req.id!)
            .then(() => {
                sync();
            })
            .catch(() => {
                showErrorToast('Cant romove element.');
            });
    };

    // const updateSprint = (sprint: Components.Schemas.WBSElementDeltaDTO) => {
    //     updateAPIRequest(sprint, WBS_ELEMENT_BASE_URL, sprint.id!)
    //         .then(() => {
    //             sync();
    //         })
    //         .catch(() => {
    //             showErrorToast('Cant remove element.');
    //         });
    // };

    const ready = portfolio?.requirementStates?.find((cur) => cur.name === 'ready');
    const newReq: Components.Schemas.RequirementDTO = { ...req, requirementState: ready, wbsElement: undefined };
    updateReq(newReq);
    // const newSprint = {
    //     ...selectedSprint,
    //     requirements: [...(selectedSprint.requirements?.filter((cur) => cur.id !== req.id) || [])],
    // };
    // updateSprint(newSprint);
};
